import { useState, useEffect, useMemo } from 'react';
import { useBlocker } from 'react-router-dom';
import { demandaService } from '../../../services/demanda';
import { useEventEmitter } from '../../../hooks/useEventBus';
import type { Demanda } from '../../../models';

interface DemandaExtended extends Demanda {
  demandados?: any[];
}

interface EditData {
  descricao: string;
  sugestao_entrega: string;
  template_respostas: {
    template_id: string;
    template_versao: string;
    preenchido_em: string;
    respostas: Record<string, unknown>;
  } | undefined;
}

interface UseDemandEditProps {
  demanda: DemandaExtended;
  isEditing: boolean;
  onSave: (updatedDemanda: Partial<DemandaExtended>) => void;
  onEditToggle: () => void;
  refetchHistorico: () => void;
  showConfirm: (type: 'info' | 'warning' | 'danger' | 'success', message: string, onConfirm: () => void, details?: string, title?: string) => void;
  setNavigationModal: (modal: { isOpen: boolean; onConfirm?: () => void }) => void;
}

export const useDemandEdit = ({
  demanda,
  isEditing,
  onSave,
  onEditToggle,
  refetchHistorico,
  showConfirm,
  setNavigationModal
}: UseDemandEditProps) => {
  const { emit } = useEventEmitter();
  
  // Estados para edição
  const [editData, setEditData] = useState<EditData>({
    descricao: demanda.descricao || '',
    sugestao_entrega: demanda.sugestao_entrega || '',
    template_respostas: demanda.template_respostas || undefined
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Bloqueador de navegação do React Router
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return hasUnsavedChanges && isEditing && currentLocation.pathname !== nextLocation.pathname;
  });

  // Função para verificar se há mudanças não salvas
  const checkForUnsavedChanges = useMemo(() => {
    const originalData = {
      descricao: demanda.descricao || '',
      sugestao_entrega: demanda.sugestao_entrega || '',
      template_respostas: demanda.template_respostas
    };
    
    // Comparar template_respostas por conteúdo
    const templateChanged = JSON.stringify(editData.template_respostas) !== JSON.stringify(originalData.template_respostas);
    
    const hasChanges = 
      editData.descricao !== originalData.descricao || 
      editData.sugestao_entrega !== originalData.sugestao_entrega ||
      templateChanged;
    
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  }, [editData, demanda]);

  // Função para salvar alterações
  const handleSave = async () => {
    if (!demanda.id) {
      setSaveError('ID da demanda não encontrado');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    
    try {
      const updatedData = {
        descricao: editData.descricao,
        sugestao_entrega: editData.sugestao_entrega,
        ...(editData.template_respostas && { 
          template_respostas: JSON.stringify(editData.template_respostas) 
        })
      };

      // Fazer o update no backend
      await demandaService.update(demanda.id, updatedData);
      
      // Emitir evento de atualização (sem buscar demanda atualizada aqui)
      // O componente pai (index.tsx) vai fazer o smartRefresh para buscar dados atualizados
      emit('demand-updated', {
        demandId: demanda.id!,
        changes: {
          type: 'edited',
          fields: ['descricao', 'sugestao_entrega', 'template_respostas'],
        }
      });
      
      // Chamar onSave para que o componente pai faça o refresh
      if (onSave) {
        const dataToSave: Partial<DemandaExtended> = {
          descricao: editData.descricao,
          sugestao_entrega: editData.sugestao_entrega
        };
        
        // Apenas incluir template_respostas se não for undefined
        if (editData.template_respostas !== undefined) {
          dataToSave.template_respostas = editData.template_respostas;
        }
        
        onSave(dataToSave);
      }
      
      // Recarregar histórico após edição
      refetchHistorico();
      
      // Resetar estado de mudanças não salvas
      setHasUnsavedChanges(false);
      
      if (onEditToggle) {
        onEditToggle();
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSaveError(error instanceof Error ? error.message : 'Erro ao salvar alterações');
    } finally {
      setIsSaving(false);
    }
  };

  // Função para cancelar edição
  const handleCancel = () => {
    // Verificar se há mudanças não salvas
    if (hasUnsavedChanges) {
      showConfirm(
        'warning',
        'Você tem alterações não salvas. Tem certeza que deseja descartar as mudanças?',
        () => {
          // Confirmou o descarte - cancelar edição
          setEditData({
            descricao: demanda.descricao || '',
            sugestao_entrega: demanda.sugestao_entrega || '',
            template_respostas: demanda.template_respostas || undefined
          });
          setSaveError(null);
          setHasUnsavedChanges(false);
          if (onEditToggle) {
            onEditToggle();
          }
        },
        'Todas as alterações feitas serão perdidas permanentemente.',
        'Descartar Alterações'
      );
    } else {
      // Não há mudanças - cancelar normalmente
      setEditData({
        descricao: demanda.descricao || '',
        sugestao_entrega: demanda.sugestao_entrega || '',
        template_respostas: demanda.template_respostas || undefined
      });
      setSaveError(null);
      if (onEditToggle) {
        onEditToggle();
      }
    }
  };

  // O checkForUnsavedChanges já monitora as mudanças automaticamente via useMemo

  // Atualizar editData quando demanda mudar
  useEffect(() => {
    setEditData({
      descricao: demanda.descricao || '',
      sugestao_entrega: demanda.sugestao_entrega || '',
      template_respostas: demanda.template_respostas || undefined
    });
    setHasUnsavedChanges(false); // Reset ao trocar de demanda
  }, [demanda]);

  // Gerenciar tentativa de navegação bloqueada
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setNavigationModal({
        isOpen: true,
        onConfirm: () => {
          setHasUnsavedChanges(false);
          blocker.proceed();
        }
      });
    }
  }, [blocker, setNavigationModal]);

  // Proteção contra saída da página com mudanças não salvas
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && isEditing) {
        const message = 'Você tem alterações não salvas. Tem certeza que deseja sair?';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isEditing]);

  return {
    // Estados
    editData,
    setEditData,
    isSaving,
    saveError,
    setSaveError,
    hasUnsavedChanges,
    
    // Funções
    checkForUnsavedChanges,
    handleSave,
    handleCancel
  };
};